<template>
    <sidebar @close="$emit('close')" v-if="collection">
        <template v-slot:title>
            {{ collection.name }}
        </template>

        <template v-slot:header>
            <!-- Tabs -->
            <div class="nav-tabs-container">
                <div class="nav-tabs-scroll-helper">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'main'}" href="#tabCollectionContentMain" id="tabCollectionLinkMain" data-toggle="tab" role="tab" aria-controls="tabCollectionContentMain" :aria-selected="activeTab == 'main'">Main</a>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <slot>
            <div class="tab-content">
                <div class="tab-pane" :class="{'show active': activeTab == 'main'}" id="tabCollectionContentMain" role="tabpanel" aria-labelledby="tabCollectionLinkMain">

                    <info-widget :collapsible="false">
                        <template v-slot:title>Collection Information</template>

                        <slot>
                            <div class="card-body py-1 mt-1">
                                <div class="row mb-0">
                                    <div class="col">
                                        <div class="form-row">
                                            <div class="col-6">
                                                <div class="mui-textfield">
                                                    <span class="textfield">
                                                        {{ collection.name }}
                                                    </span>
                                                    <label>Collection Name</label>
                                                </div>
                                            </div>
                                            <div class="col-6">
                                                <button class="btn btn-outline-primary btn-collapse-xl ml-hf" @click.prevent="editMode = 'name'; editModalOpen = true">
                                                    <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                                    <span class="btn-text">Edit</span>
                                                </button>
                                            </div>
                                        </div>
                                        <template v-if="paygroup">
                                            <div class="mui-textfield">
                                                <span class="textfield">{{ paygroup.pay_frequency_pretty }}</span>
                                                <label>Frequency</label>
                                            </div>

                                            <div class="mui-textfield">
                                                <span class="textfield">{{ payPeriodStart }}</span>
                                                <label>Pay Period Start</label>
                                            </div>

                                            <div v-if="paygroup.pay_schedule == 'semi-monthly'" class="mui-textfield">
                                                <span class="textfield">{{ payPeriodEnd }}</span>
                                                <label>Pay Period End</label>
                                            </div>

                                            <div class="mui-textfield">
                                                <span class="textfield">{{ paygroup.check_date_offset_1 }} {{ paygroup.check_date_offset_1 == 1 ? 'day' : 'days' }}</span>
                                                <label>Check Date Offset</label>
                                            </div>

                                            <div class="mui-textfield" v-if="paygroup.check_date_offset_2 !== null">
                                                <span class="textfield">{{ paygroup.check_date_offset_2 }} {{ paygroup.check_date_offset_2 == 1 ? 'day' : 'days' }}</span>
                                                <label>Second Check Date Offset</label>
                                            </div>

                                            <div class="mui-textfield">
                                                <span class="textfield">{{ paygroup.bank_holiday_treatment_pretty }}</span>
                                                <label>Bank Holiday Treatment</label>
                                            </div>

                                            <div class="mui-textfield" v-if="['semi-monthly', 'monthly'].includes(this.paygroup.pay_schedule)">
                                                <span class="textfield">{{ paygroup.weekend_treatment_pretty }}</span>
                                                <label>Weekend Treatment</label>
                                            </div>
                                        </template>
                                        <template v-else>
                                            This collection does not yet contain any paygroups.
                                        </template>
                                    </div>

                                </div>
                            </div>
                        </slot>
                    </info-widget>

                    <info-widget :collapsible="false">
                        <template v-slot:title>Pay Groups ({{ collection.paygroup_set.length || 0 }})</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editMode = 'paygroups'; editModalOpen = true">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div v-if="collection.paygroup_set.length" class="p-1">
                                <table class="table css-table widget-table table-fixed">
                                    <thead>
                                        <tr>
                                            <th>Positions</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="paygroup in collection.paygroup_set" :key="paygroup.id">
                                        <tr>
                                            <td class="truncate-wrapper">
                                                <span>{{ paygroup.name }}</span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="info-widget-empty p-2" v-else>
                                This pay group is not associated with any positions.
                            </div>
                        </slot>
                    </info-widget>
                </div>
            </div>
        </slot>

        <edit-modal
            v-if="editModalOpen"
            :collection="collection"
            :client="client"
            :mode="editMode"
            @updated="onUpdate"
            @close="editMode = ''; editModalOpen = false"
        />

    </sidebar>
</template>

<script>
import Sidebar from '@/components/Sidebar'
import InfoWidget from '@/components/InfoWidget'
import ButtonRow from '@/components/ButtonRow'
import EditModal from './EditModal'

function numerize(i) {
    const s = ('' + i).trim().slice(-1)

    if (!s) {return ''}

    const endings = {
        1: 'st',
        2: 'nd',
        3: 'rd',
    }

    const ending = endings.hasOwnProperty(s) ? endings[s] : 'th'
    return i + ending
}

export default {
    props: ['collectionId', 'client', 'tab', ],
    components: {Sidebar, InfoWidget, ButtonRow, EditModal, },
    computed: {
        payPeriodOptions() {
            if (!this.paygroup) {return []}
            if (['weekly', 'bi-weekly'].includes(this.paygroup.pay_schedule)) {
                return [
                    {text: 'Monday', value: 0},
                    {text: 'Tuesday', value: 1},
                    {text: 'Wednesday', value: 2},
                    {text: 'Thursday', value: 3},
                    {text: 'Friday', value: 4},
                    {text: 'Saturday', value: 5},
                    {text: 'Sunday', value: 6},
                ]
            }

            if (['monthly', 'semi-monthly'].includes(this.paygroup.pay_schedule)) {
                const result = []
                for (let i = 1; i <= 27; i++) {
                    result.push({text: numerize(i), value: i})
                }

                result.push({text: 'End of the Month', value: 32})

                return result
            }
            return []
        },
        payPeriodStart() {
            if (!this.paygroup) {return ''}
            const opt = this.payPeriodOptions.find(o => o.value == this.paygroup.period_start)
            if (opt) {return opt.text}
            return this.paygroup.period_start
        },
        payPeriodEnd() {
            if (!this.paygroup) {return ''}
            const opt = this.payPeriodOptions.find(o => o.value == this.paygroup.period_end)
            if (opt) {return opt.text}
            return this.paygroup.period_end
        },
        paygroup() {
            if (this.collection?.paygroup_set?.length) {
                return this.collection.paygroup_set[0]
            }
            return null
        }
    },
    data() {
        return {
            collection: null,
            activeTab: this.tab || 'main',
            editMode: '',
            editModalOpen: false,
        }
    },
    watch: {
        collectionId() {
            this.getData()
        }
    },
    mounted() {
        this.getData()
    },
    methods: {
        getData() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/settings/paygroup-collections/${this.collectionId}`).then(resp => {
                this.collection = resp
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        onUpdate(obj) {
            if (obj) {
                Object.assign(this.collection, obj)
                this.$emit('updated')
            }
            else {
                this.getData()
            }
        },
    },
}
</script>
